import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Palette, getThemePalette, DARK_SHELL_PALETTE, hexToRgba, lighten, darken, isDarkBg } from './themePalette';
import { PreviewOverlay, PreviewScreen } from './PreviewOverlay';
import { getBulletChar } from '../components/LiveSlidePreview';
import './assembling-flow.css';

export type Screen = {
  n: number;
  title: string;
  body?: string;
  bullets?: string[];
  status: 'done' | 'current' | 'pending';
};

interface AssemblingFlowLoadingProps {
  screens: Screen[];
  currentIndex: number;
  flowTitle: string;
  themeId: string | null;
  inputMode: 'prompt' | 'file';
  startTimeMs: number;
  /**
   * Generation has finished but the page is still mounted while
   * the host runs compile + export + redirect. Kill expensive
   * animations and the elapsed ticker so the main thread is free
   * for the compile/POST work.
   */
  isFinishing?: boolean;
}

const MOBILE_BREAKPOINT = 768;
const SCALE_THRESHOLD = 10;       // screens.length > this engages V5b
const VISIBLE_BAND = 3;            // strips above/below shown when scaling
const CARD_W_DESKTOP = 380;
const CARD_H_DESKTOP = 460;
const STRIP_H_DESKTOP = 42;

export function AssemblingFlowLoading({
  screens,
  currentIndex,
  flowTitle,
  themeId,
  inputMode,
  startTimeMs,
  isFinishing = false,
}: AssemblingFlowLoadingProps) {
  const [previewScreen, setPreviewScreen] = useState<PreviewScreen | null>(null);
  const [elapsedSec, setElapsedSec] = useState(() => Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000)));
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  // Tick elapsed every second — but pause when finishing so the main thread
  // is free for the host's compile + export + redirect work.
  useEffect(() => {
    if (isFinishing) return;
    const id = setInterval(() => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [startTimeMs, isFinishing]);

  // Responsive — recompute on resize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const theme = useMemo(() => getThemePalette(themeId), [themeId]);
  const darkTheme = useMemo(() => isDarkBg(theme.bg), [theme]);
  const total = screens.length;
  const fmtTime = useCallback((s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`, []);

  const stages = useMemo(() => {
    // When finishing (compile + export running), Writing copy is done; show the
    // next step as active so the user feels forward momentum during the bounce.
    const writingState = isFinishing ? 'past' as const : 'active' as const;
    const writingSub = isFinishing ? 'Done' : 'In progress';
    const imageryState = isFinishing ? 'active' as const : 'next' as const;
    const imagerySub = isFinishing ? 'Opening editor' : 'In editor';
    if (inputMode === 'file') {
      return [
        { label: 'Reading your file',  sub: 'Done',         state: 'past' as const },
        { label: 'Writing copy',       sub: writingSub,     state: writingState },
        { label: 'Generating imagery', sub: imagerySub,     state: imageryState },
      ];
    }
    return [
      { label: 'Writing copy',       sub: writingSub, state: writingState },
      { label: 'Generating imagery', sub: imagerySub, state: imageryState },
    ];
  }, [inputMode, isFinishing]);

  const openPreview = useCallback((s: Screen) => {
    if (s.status !== 'done') return;
    setPreviewScreen({ n: s.n, title: s.title, body: s.body, bullets: s.bullets });
  }, []);

  const doneCount    = screens.filter((s) => s.status === 'done').length;
  const writingCount = screens.filter((s) => s.status === 'current').length;
  const queuedCount  = screens.filter((s) => s.status === 'pending').length;

  return (
    <div className="asm-root">
      {/* Ambient theme glow — hidden during finishing to free the GPU/main
          thread for the host's compile + export work. */}
      {!isFinishing && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 360,
            transform: 'translate(-50%, 0)',
            width: 640,
            height: 640,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${hexToRgba(theme.accent, 0.16)} 0%, rgba(66,106,184,0.04) 35%, transparent 65%)`,
            pointerEvents: 'none',
            filter: 'blur(6px)',
            transition: 'background 0.3s ease',
          }}
        />
      )}

      <div
        style={{
          padding: isMobile ? '14px 16px 18px' : '26px 32px 24px',
          position: 'relative',
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >
        {/* Header */}
        <Header
          flowTitle={flowTitle}
          currentIndex={currentIndex}
          total={total}
          elapsedLabel={fmtTime(elapsedSec)}
          isMobile={isMobile}
          isFinishing={isFinishing}
          themeName={themeId ? theme.name : null}
          themeAccent={theme.accent}
        />

        {/* Stage indicator row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            marginBottom: 18,
            marginTop: 12,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          {stages.map((s, i) => (
            <React.Fragment key={s.label}>
              <StageStep label={s.label} sub={s.sub} state={s.state} />
              {i < stages.length - 1 && <StageConnector />}
            </React.Fragment>
          ))}
        </div>

        {/* Body: rail + stack */}
        {isMobile ? (
          <MobileBody
            screens={screens}
            currentIndex={currentIndex}
            theme={theme}
            themeId={themeId}
            darkTheme={darkTheme}
            openPreview={openPreview}
            elapsedLabel={fmtTime(elapsedSec)}
            isFinishing={isFinishing}
          />
        ) : (
          <DesktopBody
            screens={screens}
            currentIndex={currentIndex}
            theme={theme}
            themeId={themeId}
            darkTheme={darkTheme}
            openPreview={openPreview}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            isFinishing={isFinishing}
          />
        )}

        {/* Footer status (desktop only — mobile has its own footer inside MobileBody) */}
        {!isMobile && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 24,
              marginTop: 22,
              position: 'relative',
              fontSize: 11,
              color: '#7d7d80',
              letterSpacing: '0.04em',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ color: '#f0f0f0', fontWeight: 700 }}>{doneCount}</span> done
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#d8a06f',
                  animation: 'asm-pulseGlow 1.6s ease-in-out infinite',
                }}
              />
              <span style={{ color: '#f0f0f0', fontWeight: 700 }}>{writingCount}</span> writing
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />
              <span style={{ color: '#f0f0f0', fontWeight: 700 }}>{queuedCount}</span> queued
            </span>
          </div>
        )}
      </div>

      {previewScreen && (
        <PreviewOverlay
          screen={previewScreen}
          theme={theme}
          themeId={themeId}
          variant={isMobile ? 'mobile' : 'desktop'}
          onClose={() => setPreviewScreen(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Header
// =============================================================================
function Header({
  flowTitle,
  currentIndex,
  total,
  elapsedLabel,
  isMobile,
  isFinishing,
  themeName,
  themeAccent,
}: {
  flowTitle: string;
  currentIndex: number;
  total: number;
  elapsedLabel: string;
  isMobile: boolean;
  isFinishing: boolean;
  themeName: string | null;
  themeAccent: string;
}) {
  if (isMobile) {
    return (
      <div style={{ padding: '4px 4px 8px', position: 'relative' }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#7d7d80',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Assembling your flow
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.015em',
            lineHeight: 1.15,
          }}
        >
          {flowTitle}
        </div>
        {themeName && <ThemePill name={themeName} accent={themeAccent} style={{ marginTop: 8 }} />}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: 4,
        position: 'relative',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#7d7d80',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Assembling your flow
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {flowTitle}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {themeName && <ThemePill name={themeName} accent={themeAccent} />}
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: 10,
              color: '#7d7d80',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 700,
              marginBottom: 2,
            }}
          >
            Screen
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
              color: '#f0f0f0',
              lineHeight: 1,
            }}
          >
            {Math.min(currentIndex + 1, total)}
            <span style={{ color: '#5a5a5d', fontWeight: 600 }}> / {total}</span>
          </div>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(216,160,111,0.10)',
            border: '1px solid rgba(216,160,111,0.25)',
            color: '#e6c19a',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#d8a06f',
              animation: isFinishing ? 'none' : 'asm-pulseGlow 1.6s ease-in-out infinite',
            }}
          />
          {isFinishing ? 'Opening editor…' : `Generating · ${elapsedLabel}`}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Theme name pill — small badge showing the AI's picked theme so the user
// can verify what styling they're getting before they land in the editor.
// =============================================================================
function ThemePill({
  name,
  accent,
  style,
}: {
  name: string;
  accent: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: '#b9b9bb',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: accent,
          flexShrink: 0,
        }}
      />
      Theme · <span style={{ color: '#f0f0f0' }}>{name}</span>
    </div>
  );
}

// =============================================================================
// Stage indicator
// =============================================================================
function StageStep({
  label,
  sub,
  state,
}: {
  label: string;
  sub: string;
  state: 'active' | 'next' | 'future' | 'past';
}) {
  const isActive = state === 'active';
  const isPast = state === 'past';
  const dotColor =
    isActive ? '#d8a06f' :
    isPast   ? '#10b981' :
    state === 'next' ? 'rgba(255,255,255,0.30)' :
    'rgba(255,255,255,0.15)';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        minWidth: 80,
        padding: '0 4px',
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dotColor,
          boxShadow: isActive ? '0 0 0 4px rgba(216,160,111,0.12)' : 'none',
          animation: isActive ? 'asm-pulseGlow 1.8s ease-in-out infinite' : 'none',
        }}
      />
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: isActive || isPast ? '#f0f0f0' : state === 'next' ? '#9a9a9d' : '#5a5a5d',
          }}
        >
          {label}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 9,
              color: isActive ? '#e6c19a' : isPast ? '#7d7d80' : '#5a5a5d',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              marginTop: 2,
              fontWeight: 700,
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function StageConnector() {
  return (
    <div
      style={{
        width: 48,
        height: 1,
        marginTop: -22,
        background: 'linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06))',
      }}
    />
  );
}

// =============================================================================
// NavRail — vertical dashed nav rail on left edge (both desktop and mobile)
// =============================================================================
function NavRail({
  screens,
  theme,
  openPreview,
  variant,
}: {
  screens: Screen[];
  theme: Palette;
  openPreview: (s: Screen) => void;
  variant: 'desktop' | 'mobile';
}) {
  const minHeight = variant === 'mobile' ? 40 : 36;
  return (
    <div
      style={{
        width: 28,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        paddingTop: 4,
        paddingBottom: 4,
        flexShrink: 0,
      }}
      aria-label="Screen navigation rail"
    >
      {screens.map((s) => {
        const done = s.status === 'done';
        const cur = s.status === 'current';
        return (
          <button
            key={s.n}
            type="button"
            className="asm-rail-btn"
            data-done={done}
            onClick={done ? () => openPreview(s) : undefined}
            disabled={!done}
            aria-label={done ? `Preview screen ${s.n}: ${s.title}` : `Screen ${s.n}`}
            style={{ minHeight }}
          >
            <div
              style={{
                width: 0,
                height: '100%',
                position: 'relative',
                borderLeft: done
                  ? `2px solid ${theme.accent}`
                  : cur
                    ? '2px solid transparent'
                    : '2px dashed rgba(255,255,255,0.20)',
                opacity: done ? 0.9 : 1,
                transition: 'border-color 0.3s ease',
              }}
            >
              {cur && (
                <div
                  style={{
                    position: 'absolute',
                    left: -1,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: `linear-gradient(180deg, transparent, ${theme.accent} 50%, transparent)`,
                    backgroundSize: '100% 200%',
                    animation: 'asm-shimmerV 1.8s linear infinite',
                  }}
                />
              )}
              {done && (
                <div
                  style={{
                    position: 'absolute',
                    left: -3,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: theme.accent,
                    opacity: 0.6,
                  }}
                />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Strip — used for done strips above and queued strips below
// =============================================================================
function CardStrip({
  screen,
  kind,
  depth,
  theme,
  darkTheme,
  hovered,
  onHover,
  onClick,
}: {
  screen: Screen;
  kind: 'done' | 'pending';
  depth: number;
  theme: Palette;
  darkTheme: boolean;
  hovered: boolean;
  onHover: (n: number | null) => void;
  onClick?: () => void;
}) {
  const isDone = kind === 'done';
  const taper = depth * 10;
  const w = CARD_W_DESKTOP - taper;
  const sat = Math.max(0.4, 1 - depth * 0.1);
  const op = Math.max(0.55, 1 - depth * 0.06);

  return (
    <div
      onClick={isDone ? onClick : undefined}
      onMouseEnter={isDone ? () => onHover(screen.n) : undefined}
      onMouseLeave={isDone ? () => onHover(null) : undefined}
      className="asm-strip"
      style={{
        width: w + (hovered ? 10 : 0),
        height: STRIP_H_DESKTOP,
        borderTopLeftRadius: 18,
        borderTopRightRadius: 18,
        background: isDone ? theme.bg : 'transparent',
        border: isDone
          ? `1px solid ${darkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
          : '1px dashed rgba(255,255,255,0.14)',
        borderBottom: 'none',
        boxShadow: isDone
          ? `0 -1px 0 ${darkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.6)'} inset, 0 ${(hovered ? 12 : 6) + depth * 2}px ${14 + depth * 4}px -12px ${theme.shadow}`
          : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 18px',
        position: 'relative',
        zIndex: hovered ? 25 : 10 - depth,
        opacity: hovered ? 1 : op,
        filter: isDone ? `saturate(${hovered ? 1 : sat})` : 'none',
        cursor: isDone ? 'pointer' : 'default',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.08em',
            color: isDone ? theme.secondary : '#5a5a5d',
          }}
        >
          {String(screen.n).padStart(2, '0')}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          {screen.title ? (
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: isDone ? theme.primary : '#7d7d80',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.01em',
                lineHeight: 1,
                maxWidth: 220,
              }}
            >
              {screen.title}
            </span>
          ) : (
            <span className="asm-skeleton" style={{ width: 140, height: 10, lineHeight: 1 }} />
          )}
          <div
            style={{
              height: 2,
              width: 80,
              background: isDone
                ? (darkTheme ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)')
                : 'rgba(255,255,255,0.07)',
              borderRadius: 1,
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {hovered && isDone && (
          <span
            style={{
              fontSize: 9,
              padding: '2px 7px',
              borderRadius: 4,
              background: hexToRgba(theme.accent, 0.18),
              color: darkTheme ? lighten(theme.accent, 0.2) : darken(theme.accent, 0.25),
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            Preview
          </span>
        )}
        {isDone ? (
          <CheckMarkIcon color={theme.accent} />
        ) : (
          <span
            style={{
              fontSize: 9,
              padding: '2px 7px',
              borderRadius: 4,
              background: 'rgba(255,255,255,0.05)',
              color: '#7d7d80',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            Up next
          </span>
        )}
      </div>
    </div>
  );
}

// Render an inline string that may contain [text](url) markdown links as
// React fragments — the link text is styled with the theme accent and an
// underline so it visually reads as a link, but no href is attached. The
// preview is a transient view that's about to be replaced by the editor,
// so making links non-interactive avoids click-races mid-redirect.
function renderInlineMarkdown(text: string, accent: string): React.ReactNode {
  if (!text) return text;
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let k = 0;
  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    out.push(
      <span
        key={`link-${k++}`}
        style={{
          color: accent,
          textDecoration: 'underline',
          textUnderlineOffset: 2,
          textDecorationThickness: 1,
        }}
      >
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out.length > 0 ? out : text;
}

// Pre-content loading placeholder — used in the hero card slot before the
// first screen's text streams in (typing isn't parsed yet, no done screens
// to fall back on). Mirrors the .ai-generating-spinner SVG pattern from
// ai.css so the visual language stays consistent across the AI flow.
function ActiveCardLoading({
  theme,
  fullWidth = false,
}: {
  theme: Palette;
  fullWidth?: boolean;
}) {
  const widthVal: number | string = fullWidth ? '100%' : CARD_W_DESKTOP;
  const heightVal: number | string = fullWidth ? '100%' : CARD_H_DESKTOP;
  return (
    <div
      style={{
        width: widthVal,
        height: heightVal,
        minHeight: fullWidth ? 340 : undefined,
        borderRadius: fullWidth ? 22 : 18,
        background: theme.bg,
        boxShadow: `
          0 0 0 1px rgba(216,160,111,0.20),
          0 30px 60px -20px rgba(0,0,0,0.5)
        `,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        color: theme.secondary,
      }}
    >
      <svg viewBox="0 0 50 50" width="40" height="40" style={{ animation: 'spin 1.5s linear infinite', color: theme.accent }}>
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="80 40"
        />
      </svg>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: theme.secondary,
        }}
      >
        Loading…
      </span>
    </div>
  );
}

function CheckMarkIcon({ color, size = 12 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16">
      <path
        d="M3.5 8.5l3 3 6-6.5"
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// =============================================================================
// Desktop body — V5 (with V5b auto-scale)
// =============================================================================
function DesktopBody({
  screens,
  currentIndex,
  theme,
  themeId,
  darkTheme,
  openPreview,
  hoveredId,
  setHoveredId,
  isFinishing,
}: {
  screens: Screen[];
  currentIndex: number;
  theme: Palette;
  themeId: string | null;
  darkTheme: boolean;
  openPreview: (s: Screen) => void;
  hoveredId: number | null;
  setHoveredId: (n: number | null) => void;
  isFinishing: boolean;
}) {
  const allDoneScreens = screens.filter((s) => s.status === 'done');
  const pendingScreens = screens.filter((s) => s.status === 'pending');
  const typingScreen = screens.find((s) => s.status === 'current');

  // Surface the last completed screen into the hero slot when generation is
  // winding down — either fully finishing (isFinishing) or all expected
  // screens have completed and we're waiting on the stream's closing
  // brackets. Catches the brief gap before isComplete=true that the user
  // saw as "flash blank then go back to last screen".
  const noPendingLeft = pendingScreens.length === 0;
  const useFinishingFallback = !typingScreen && (isFinishing || noPendingLeft) && allDoneScreens.length > 0;
  const finishingFallback = useFinishingFallback ? allDoneScreens[allDoneScreens.length - 1] : null;
  const currentScreen = typingScreen || finishingFallback;
  const doneScreens = finishingFallback ? allDoneScreens.slice(0, -1) : allDoneScreens;

  const useScale = screens.length > SCALE_THRESHOLD;

  // V5b telescoping: show only VISIBLE_BAND closest done/pending
  const visibleDone    = useScale ? doneScreens.slice(-VISIBLE_BAND) : doneScreens;
  const visiblePending = useScale ? pendingScreens.slice(0, VISIBLE_BAND) : pendingScreens;
  const hiddenDone     = useScale ? Math.max(0, doneScreens.length - VISIBLE_BAND) : 0;
  const hiddenPending  = useScale ? Math.max(0, pendingScreens.length - VISIBLE_BAND) : 0;

  const stackAreaStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: 60, // leave room for the rail on the left
    paddingRight: 32,
    minHeight: 600,
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Rail — absolute-positioned over the left edge of the stack area */}
      <div
        style={{
          position: 'absolute',
          left: 16,
          top: 8,
          bottom: 8,
          width: 28,
          zIndex: 6,
          display: 'flex',
        }}
      >
        <NavRail screens={screens} theme={theme} openPreview={openPreview} variant="desktop" />
      </div>

      <div style={stackAreaStyle}>
        {/* Hidden-done sliver */}
        {hiddenDone > 0 && (
          <TelescopeSliver
            label={`+ ${hiddenDone} more done`}
            kind="done"
            theme={theme}
            darkTheme={darkTheme}
          />
        )}

        {/* Done strips */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: -1 }}>
          {visibleDone.map((s, idx) => {
            const depth = visibleDone.length - 1 - idx;
            return (
              <CardStrip
                key={s.n}
                screen={s}
                kind="done"
                depth={depth}
                theme={theme}
                darkTheme={darkTheme}
                hovered={hoveredId === s.n}
                onHover={setHoveredId}
                onClick={() => openPreview(s)}
              />
            );
          })}
        </div>

        {/* Active hero card (or pre-content loading placeholder).
            `key` is set so React remounts the card when the displayed screen
            changes, which lets the .asm-fade-in animation re-run on each
            new screen and on the loading→first-content swap. */}
        <div
          style={{
            position: 'relative',
            animation: isFinishing || !currentScreen ? 'none' : 'asm-float 6s ease-in-out infinite',
            zIndex: 20,
          }}
        >
          {currentScreen ? (
            <div key={`card-${currentScreen.n}`} className="asm-fade-in">
              <ActiveCard screen={currentScreen} theme={theme} themeId={themeId} darkTheme={darkTheme} isFinishing={isFinishing} />
            </div>
          ) : (
            <div key="card-loading" className="asm-fade-in">
              <ActiveCardLoading theme={theme} />
            </div>
          )}
        </div>

        {/* Queued strips below — mirror with scaleY flip */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: -1,
            transform: 'scaleY(-1)',
          }}
        >
          {visiblePending.map((s, idx) => (
            <div key={s.n} style={{ transform: 'scaleY(-1)' }}>
              <CardStrip
                screen={s}
                kind="pending"
                depth={idx}
                theme={theme}
                darkTheme={darkTheme}
                hovered={false}
                onHover={() => {}}
              />
            </div>
          ))}
        </div>

        {/* Hidden-pending sliver */}
        {hiddenPending > 0 && (
          <TelescopeSliver
            label={`+ ${hiddenPending} more queued`}
            kind="pending"
            theme={theme}
            darkTheme={darkTheme}
          />
        )}
      </div>
    </div>
  );
}

function TelescopeSliver({
  label,
  kind,
  theme,
  darkTheme,
}: {
  label: string;
  kind: 'done' | 'pending';
  theme: Palette;
  darkTheme: boolean;
}) {
  const isDone = kind === 'done';
  return (
    <div
      style={{
        width: CARD_W_DESKTOP - 80,
        height: 18,
        borderRadius: 10,
        marginBottom: kind === 'done' ? 6 : 0,
        marginTop: kind === 'pending' ? 6 : 0,
        background: isDone ? hexToRgba(theme.accent, 0.08) : 'transparent',
        border: isDone
          ? `1px solid ${hexToRgba(theme.accent, 0.18)}`
          : '1px dashed rgba(255,255,255,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color: isDone ? (darkTheme ? lighten(theme.accent, 0.2) : darken(theme.accent, 0.25)) : '#7d7d80',
        opacity: 0.85,
      }}
    >
      {label}
    </div>
  );
}

// =============================================================================
// Active card (hero) — shared between desktop and mobile (different sizing)
// =============================================================================
function ActiveCard({
  screen,
  theme,
  themeId,
  darkTheme,
  fullWidth = false,
  isFinishing = false,
}: {
  screen: Screen;
  theme: Palette;
  themeId: string | null;
  darkTheme: boolean;
  fullWidth?: boolean;
  isFinishing?: boolean;
}) {
  const widthVal: number | string = fullWidth ? '100%' : CARD_W_DESKTOP;
  const heightVal: number | string = fullWidth ? '100%' : CARD_H_DESKTOP;
  return (
    <div
      style={{
        width: widthVal,
        height: heightVal,
        minHeight: fullWidth ? 340 : undefined,
        borderRadius: fullWidth ? 22 : 18,
        background: theme.bg,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: `
          0 0 0 1px rgba(216,160,111,0.30),
          0 30px 60px -20px rgba(0,0,0,0.6),
          0 0 110px -10px ${hexToRgba(theme.accent, 0.35)}
        `,
        transition: 'background 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header strip — screen number left, writing pill right */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: fullWidth ? '16px 20px 0' : '14px 18px 0',
          position: 'relative',
          zIndex: 5,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: theme.secondary,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.08em',
            opacity: 0.7,
          }}
        >
          SCREEN {String(screen.n).padStart(2, '0')}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: hexToRgba(theme.accent, 0.14),
            border: `1px solid ${hexToRgba(theme.accent, 0.45)}`,
            fontSize: 10,
            fontWeight: 700,
            color: darkTheme ? lighten(theme.accent, 0.2) : darken(theme.accent, 0.25),
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: theme.accent,
              animation: isFinishing ? 'none' : 'asm-pulseGlow 1.6s ease-in-out infinite',
            }}
          />
          {isFinishing ? 'Almost ready' : 'Writing copy'}
        </span>
      </div>

      {/* Body — title + typing text (or bullet list when present) */}
      <div
        style={{
          flex: 1,
          padding: fullWidth ? '32px 26px 28px' : '36px 32px 28px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <h1
          style={{
            margin: '0 0 16px',
            fontSize: fullWidth ? 23 : 22,
            lineHeight: fullWidth ? 1.22 : 1.25,
            fontWeight: 700,
            color: theme.primary,
            letterSpacing: '-0.01em',
            textAlign: 'center',
            wordBreak: 'break-word',
            minHeight: '1.25em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {screen.title || (
            <>
              <span
                className="asm-skeleton"
                style={{ width: '60%', height: '0.85em', maxWidth: 260 }}
              />
              <span
                className="asm-caret"
                style={{ background: theme.primary }}
              />
            </>
          )}
        </h1>
        {screen.bullets && screen.bullets.length > 0 ? (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              textAlign: 'left',
              minHeight: '5em',
            }}
          >
            {screen.bullets.map((item, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: theme.secondary,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    color: theme.accent,
                    fontWeight: 700,
                    lineHeight: 1.5,
                    minWidth: '1em',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {getBulletChar(themeId || undefined, i)}
                </span>
                <span style={{ flex: 1 }}>
                  {renderInlineMarkdown(item, theme.accent)}
                  {i === screen.bullets!.length - 1 && screen.status === 'current' && (
                    <span className="asm-caret" style={{ background: theme.primary, marginLeft: 2 }} />
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.65,
              color: theme.secondary,
              textAlign: 'center',
              minHeight: '5em',
            }}
          >
            <span className="asm-typing-line">
              {screen.body ? renderInlineMarkdown(screen.body, theme.accent) : ''}
              <span className="asm-caret" style={{ background: theme.primary }} />
            </span>
          </p>
        )}
      </div>

      {/* Bottom shimmer — only while actively writing */}
      {!isFinishing && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${theme.accent} 50%, transparent)`,
            backgroundSize: '200% 100%',
            animation: 'asm-shimmer 1.8s linear infinite',
            opacity: 0.85,
          }}
        />
      )}

      {/* Inner highlight */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: fullWidth ? 22 : 18,
          pointerEvents: 'none',
          boxShadow: darkTheme
            ? 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.4)'
            : 'inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.05)',
        }}
      />
    </div>
  );
}

// =============================================================================
// Mobile body — V5c
// =============================================================================
function MobileBody({
  screens,
  currentIndex,
  theme,
  themeId,
  darkTheme,
  openPreview,
  elapsedLabel,
  isFinishing,
}: {
  screens: Screen[];
  currentIndex: number;
  theme: Palette;
  themeId: string | null;
  darkTheme: boolean;
  openPreview: (s: Screen) => void;
  elapsedLabel: string;
  isFinishing: boolean;
}) {
  // Use status === 'current' as the sole signal for the live typing screen.
  // The previous `screens[currentIndex]` fallback picked up the next pending
  // screen at the very start of generation (when typing hasn't been parsed
  // yet) and rendered its "Screen 1" placeholder title in the hero card.
  const typingScreen = screens.find((s) => s.status === 'current');

  const lastDoneIdx = (() => {
    for (let i = screens.length - 1; i >= 0; i--) {
      if (screens[i].status === 'done') return i;
    }
    return -1;
  })();
  // Fire the fallback when we're winding down (isFinishing) OR when all
  // expected screens have completed and we're waiting for the closing
  // brackets of the JSON stream. Catches the brief gap between "last screen
  // done" and "isComplete=true" where the page used to flash blank.
  const noPendingLeft = !screens.some((s) => s.status === 'pending');
  const usingFinishingFallback = !typingScreen && (isFinishing || noPendingLeft) && lastDoneIdx >= 0;
  const current = typingScreen || (usingFinishingFallback ? screens[lastDoneIdx] : null);
  const activeIdx = usingFinishingFallback ? lastDoneIdx : currentIndex;
  const prevDone = activeIdx > 0 ? screens[activeIdx - 1] : null;
  const nextUp = activeIdx + 1 < screens.length ? screens[activeIdx + 1] : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        position: 'relative',
        minHeight: '60vh',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          padding: '0 4px 0 0',
          position: 'relative',
          minHeight: 0,
          gap: 4,
        }}
      >
        <NavRail screens={screens} theme={theme} openPreview={openPreview} variant="mobile" />

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingLeft: 4,
            minWidth: 0,
          }}
        >
          {/* Peek above — last done card */}
          {prevDone && prevDone.status === 'done' && (
            <button
              type="button"
              onClick={() => openPreview(prevDone)}
              aria-label={`Preview screen ${prevDone.n}: ${prevDone.title}`}
              style={{
                all: 'unset',
                cursor: 'pointer',
                width: 'calc(100% - 18px)',
                alignSelf: 'center',
                height: 26,
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
                background: theme.bg,
                border: `1px solid ${darkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                borderBottom: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                opacity: 0.55,
                filter: 'saturate(0.7)',
                marginBottom: -2,
                zIndex: 1,
                boxSizing: 'border-box',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.primary,
                  overflow: 'hidden',
                  letterSpacing: '-0.01em',
                  minWidth: 0,
                }}
              >
                <span>{String(prevDone.n).padStart(2, '0')}</span>
                {prevDone.title ? (
                  <>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prevDone.title}</span>
                  </>
                ) : (
                  <span className="asm-skeleton" style={{ width: 110, height: 8 }} />
                )}
              </span>
              <CheckMarkIcon color={theme.accent} size={11} />
            </button>
          )}

          {/* Active hero card (or pre-content loading placeholder).
              `key` triggers a fresh mount per screen swap so .asm-fade-in
              replays on the loading→first-content transition. */}
          <div
            style={{
              width: '100%',
              flex: 1,
              animation: isFinishing || !current ? 'none' : 'asm-float 6s ease-in-out infinite',
              position: 'relative',
              zIndex: 2,
              minHeight: 340,
              display: 'flex',
            }}
          >
            {current ? (
              <div key={`card-${current.n}`} className="asm-fade-in" style={{ width: '100%', display: 'flex' }}>
                <ActiveCard screen={current} theme={theme} themeId={themeId} darkTheme={darkTheme} fullWidth isFinishing={isFinishing} />
              </div>
            ) : (
              <div key="card-loading" className="asm-fade-in" style={{ width: '100%', display: 'flex' }}>
                <ActiveCardLoading theme={theme} fullWidth />
              </div>
            )}
          </div>

          {/* Peek below — next queued card */}
          {nextUp && nextUp.status === 'pending' && (
            <div
              style={{
                width: 'calc(100% - 18px)',
                alignSelf: 'center',
                height: 26,
                borderBottomLeftRadius: 18,
                borderBottomRightRadius: 18,
                background: 'transparent',
                border: '1px dashed rgba(255,255,255,0.14)',
                borderTop: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                marginTop: -2,
                zIndex: 1,
                boxSizing: 'border-box',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#7d7d80',
                  overflow: 'hidden',
                  letterSpacing: '-0.01em',
                  minWidth: 0,
                }}
              >
                <span>{String(nextUp.n).padStart(2, '0')}</span>
                {nextUp.title ? (
                  <>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextUp.title}</span>
                  </>
                ) : (
                  <span className="asm-skeleton" style={{ width: 110, height: 8 }} />
                )}
              </span>
              <span
                style={{
                  fontSize: 8,
                  padding: '2px 6px',
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.05)',
                  color: '#7d7d80',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                Up next
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile footer */}
      <div
        style={{
          padding: '18px 4px 4px 38px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
          color: '#9a9a9d',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>
          <span style={{ color: '#f0f0f0', fontWeight: 700 }}>Screen {Math.min(currentIndex + 1, screens.length)}</span>
          <span style={{ color: '#5a5a5d', fontWeight: 500 }}> of {screens.length}</span>
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: '#e6c19a',
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#d8a06f',
              animation: isFinishing ? 'none' : 'asm-pulseGlow 1.6s ease-in-out infinite',
            }}
          />
          {isFinishing ? 'Opening editor…' : `Generating · ${elapsedLabel}`}
        </span>
      </div>
    </div>
  );
}

// Touch unused-import warnings (DARK_SHELL_PALETTE is consumed via getThemePalette)
void DARK_SHELL_PALETTE;
